import { api } from '../api';
import { validate as isUUID } from 'uuid';

export interface Visitor {
  id: string;
  name: string;
  document_number: string;
  phone_number: string;
  status_flag: 'ACTIVE' | 'INACTIVE';
  start_date: string;
  end_date: string;
  tenant: {
    id: string;
    name: string;
  };
  user: {
    id: string;
    name: string;
  };
  areas: Array<{
    id: string;
    name: string;
  }>;
  images?: string[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateVisitorData {
  name: string;
  document: string;
  phone_number: string;
  status_flag: 'ACTIVE' | 'INACTIVE';
  start_date: string;
  end_date: string;
  tenant_id: string;
  user_id: string;
  area_id: string | string[];
  images?: File[];
}

export interface UpdateVisitorData extends Partial<CreateVisitorData> {}

interface ListVisitorsParams {
  page?: number;
  limit?: number;
  search?: string;
}

const validateAreaIds = (areaIds: string | string[]): boolean => {
  if (Array.isArray(areaIds)) {
    return areaIds.every(id => isUUID(id));
  }
  return isUUID(areaIds);
};

export const visitorService = {
  list: async (params?: ListVisitorsParams) => {
    const response = await api.get('/visitors', { params });
    return response.data;
  },

  getById: async (id: string) => {
    const response = await api.get(`/visitors/${id}`);
    return response.data;
  },

  create: async (data: CreateVisitorData) => {
    // Validar area_id antes de criar
    if (data.area_id && !validateAreaIds(data.area_id)) {
      throw new Error('IDs de área inválidos fornecidos');
    }

    const formData = new FormData();
    
    // Adicionar campos básicos
    formData.append('name', data.name);
    formData.append('document', data.document);
    formData.append('phone_number', data.phone_number);
    formData.append('status_flag', data.status_flag);
    formData.append('start_date', data.start_date);
    formData.append('end_date', data.end_date);
    formData.append('user_id', data.user_id);
    
    // Tratar area_id - pode ser string ou array
    if (Array.isArray(data.area_id)) {
      data.area_id.forEach(areaId => {
        formData.append('area_id', areaId);
      });
    } else {
      formData.append('area_id', data.area_id);
    }
    
    // Adicionar imagens se houver
    if (data.images && data.images.length > 0) {
      data.images.forEach(image => {
        formData.append('images', image);
      });
    }

    const response = await api.post('/visitors', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  update: async (id: string, data: UpdateVisitorData) => {
    // Validar area_id antes de atualizar
    if (data.area_id && !validateAreaIds(data.area_id)) {
      throw new Error('IDs de área inválidos fornecidos');
    }

    const formData = new FormData();
    
    // Adicionar apenas campos que foram fornecidos
    if (data.name) formData.append('name', data.name);
    if (data.document) formData.append('document', data.document);
    if (data.phone_number) formData.append('phone_number', data.phone_number);
    if (data.status_flag) formData.append('status_flag', data.status_flag);
    if (data.start_date) formData.append('start_date', data.start_date);
    if (data.end_date) formData.append('end_date', data.end_date);
    if (data.user_id) formData.append('user_id', data.user_id);
    
    // Tratar area_id
    if (data.area_id) {
      if (Array.isArray(data.area_id)) {
        data.area_id.forEach(areaId => {
          formData.append('area_id', areaId);
        });
      } else {
        formData.append('area_id', data.area_id);
      }
    }
    
    // Adicionar imagens se houver
    if (data.images && data.images.length > 0) {
      data.images.forEach(image => {
        formData.append('images', image);
      });
    }

    const response = await api.post(`/visitors/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  delete: async (id: string) => {
    const response = await api.delete(`/visitors/${id}`);
    return response.data;
  },
};